import Button from "@/Components/Button/Button";
import Select from "@/Components/Select/Select";
import Spinner from "@/Components/Spinner/Spinner";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-grid-system";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";
import ModalTitle from "./ModalTitle";

import Checkbox from "@/Components/Checkbox/Checkbox";
import Collapse from "@/Components/Collapse/Collapse";
import IconButton from "@/Components/IconButton/IconButton";
import Textfield from "@/Components/Input/Textfield";
import useBranchId from "@/shared/hooks/useBranchId";
import usePrinterSettingsHook from "@/shared/hooks/usePrinterSettingsHook";
import useReceiptsHook from "@/shared/hooks/useReceiptsHook";
import { Controller, useForm } from "react-hook-form";
import { useImmer } from "use-immer";
import useElectronHook from "../../shared/hooks/useElectronHook";
import Boolean from "../Boolean/BooleanWithLabel";
import Icon from "../Icon/Icon";

interface PrinterProps {
  [key: string]: {
    quantity: number;
    receiptId: number;
    configName: string;
  }[];
}

const PrinterSettingModal = ({ onExit, ...props }) => {
  const { t } = useTranslation("");
  const branch = useBranchId();

  const { query } = useReceiptsHook();
  const { isElectron } = useElectronHook();
  const { handleSubmit, register, control, errors } = useForm();
  const { getPrinterSettings, postPrinterSettings } = usePrinterSettingsHook();
  const [selectedPrinters, setSelectedPrinters] = useState<string[]>([]);
  const [isActive, setIsActive] = useState<boolean>(null);
  const [printers, setPrinters] = useImmer<PrinterProps>(null);

  useEffect(() => {
    if (getPrinterSettings.data && getPrinterSettings.isSuccess) {
      const set = new Set<string>(getPrinterSettings.data.chosenPrinters);
      setSelectedPrinters(Array.from(set));
      setPrinters(getPrinterSettings.data.printers);
      setIsActive(getPrinterSettings.data.isActive);
    }
  }, [getPrinterSettings.data, getPrinterSettings.isSuccess]);

  useEffect(() => {
    getPrinterSettings.refetch();
  }, [branch]);

  const receiptSettingOptions = useMemo(() => {
    if (query.data) {
      return query.data.map((item) => ({
        label: item.name,
        value: item.item_id,
      }));
    }
  }, [query.data]);

  const handleClickAdd = (printerName) => {
    const isSelected = selectedPrinters.includes(printerName);
    if (!isSelected) {
      setSelectedPrinters([...selectedPrinters, printerName]);
    }
    setPrinters((draft) => {
      draft[printerName] = [
        ...draft[printerName],
        { quantity: 1, configName: "", receiptId: 1 },
      ];
    });
  };

  const handleClickCheckbox = (printerName, isChecked) => {
    if (isChecked) {
      setSelectedPrinters([...selectedPrinters, printerName]);
      if (printers[printerName].length === 0) {
        setPrinters((draft) => {
          draft[printerName] = [
            ...draft[printerName],
            { quantity: 1, configName: "", receiptId: 1 },
          ];
        });
      }
    } else {
      setSelectedPrinters(selectedPrinters.filter((i) => i !== printerName));
      setPrinters((draft) => {
        draft[printerName] = printers[printerName].filter((i) => i.configName);
      });
    }
  };

  const handleClickDelete = (printerName, index) => {
    if (index > 0) {
      setPrinters((draft) => {
        draft[printerName] = draft[printerName]
          .slice(0, index)
          .concat(draft[printerName].slice(index + 1));
      });
    } else {
      setSelectedPrinters(selectedPrinters.filter((i) => i !== printerName));
      setPrinters((draft) => {
        draft[printerName] = [];
      });
    }
  };

  const onSubmitForm = (data) => {
    const retData: any = {
      configuration_names: [],
      print_quantities: [],
      receipt_setting_ids: [],
      chosen_printers: [],
    };

    selectedPrinters.forEach((selected) => {
      const selectedData = data[selected.replaceAll(".", "?**")];
      selectedData.configName.map((i, index) => {
        retData.chosen_printers.push(selected.replaceAll("?**", "."));
        retData.configuration_names.push(selectedData.configName[index]);
        retData.print_quantities.push(selectedData.quantity[index]);
        retData.receipt_setting_ids.push(
          selectedData.receiptId[index]?.value || selectedData.receiptId[index]
        );
      });
    });

    const submitData = {
      ...retData,
      printers: Object.keys(printers),
      is_active: isActive,
      branch_id: getPrinterSettings.data.branchId,
    };

    postPrinterSettings.mutateAsync(submitData, {
      onSuccess: () => {
        onExit();
      },
    });
  };

  return (
    <Modal onExit={onExit} width="80rem" {...props}>
      <ModalTitle mb="2rem" p="0">
        {t("orders_live.printerSettings")}
        {isElectron && (
          <div
            onClick={() => {
              window.api.send("getPrinters", null);
            }}
            className="inline cursor-pointer"
          >
            <Icon className="ml-8 h-7 w-7" variants="sync" />
          </div>
        )}
      </ModalTitle>
      {getPrinterSettings.isPending && <Spinner />}
      {getPrinterSettings.isError && <div>error</div>}
      {printers && (
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <div className="mb-[2rem]">
            <Boolean
              onChange={(e) => setIsActive(e.target.checked)}
              checked={isActive}
            >
              {t("active")}
            </Boolean>
          </div>
          {Object.keys(printers).map((name, index) => (
            <div
              key={`${name}-${index}`}
              className="mb-6 flex items-start gap-4"
            >
              <Checkbox
                name={name}
                active={selectedPrinters.includes(name)}
                checked={selectedPrinters.includes(name)}
                onChange={(e) => handleClickCheckbox(name, e.target.checked)}
              />
              <div className="w-full mb-[10px]">
                <div className="flex items-center">
                  <h3 className="py-0 my-0 mb-[1rem]">{name}</h3>
                  <div
                    onClick={() => handleClickAdd(name)}
                    className="ml-2 mb-[1rem]"
                  >
                    <IconButton type="button" variants={"plus"} size="small" />
                  </div>
                </div>
                <Collapse active={selectedPrinters.includes(name)}>
                  {printers[name].map((item, index) => {
                    const tempName = name.replaceAll(".", "?**");
                    return (
                      <Row>
                        <Col md={4}>
                          <Textfield
                            placeholder={t("configurationName")}
                            name={`${tempName}.configName.${index}`}
                            ref={register({
                              required: {
                                value: selectedPrinters.includes(name),
                                message: t("fieldRequired"),
                              },
                            })}
                            defaultValue={item.configName}
                            error={
                              errors?.[tempName]?.["configName"]?.[index]
                                ?.message
                            }
                          />
                        </Col>
                        <Col md={3}>
                          <Textfield
                            type="number"
                            placeholder={t("quantity")}
                            name={`${tempName}.quantity.${index}`}
                            ref={register({
                              required: {
                                value: selectedPrinters.includes(name),
                                message: t("fieldRequired"),
                              },
                            })}
                            defaultValue={item.quantity}
                            error={
                              errors?.[tempName]?.["quantity"]?.[index]?.message
                            }
                          />
                        </Col>
                        <Col md={3}>
                          <Controller
                            name={`${tempName}.receiptId.${index}`}
                            control={control}
                            defaultValue={item.receiptId}
                            render={(props) => {
                              return (
                                <Select
                                  value={receiptSettingOptions?.find(
                                    (i) => i.value === props.value
                                  )}
                                  options={receiptSettingOptions}
                                  onChange={(v) => props.onChange(v)}
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                  menuPortalTarget={document.getElementById(
                                    "root"
                                  )}
                                />
                              );
                            }}
                          />
                        </Col>
                        <Col md={2}>
                          <IconButton
                            onClick={() => handleClickDelete(name, index)}
                            type="button"
                            variants="minus"
                            size="small"
                          />
                        </Col>
                      </Row>
                    );
                  })}
                </Collapse>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-end">
            <Button
              isDisabled={!(selectedPrinters.length > 0)}
              type="submit"
              isPending={postPrinterSettings.isPending}
            >
              {t("save")}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default PrinterSettingModal;
